import { Barcode, Cube, Package, Tray, Truck } from '@phosphor-icons/react';
import styled from 'styled-components';
import { Fragment, useMemo, useState } from 'react';
import {
  ColoredPill,
  HeaderText,
  HoverBorderButton,
  MuteBorder,
  MuteText,
  ShadowBox,
} from '../../../elements';
import { CommonUtility, theme } from '../../../utility';
import { DarkPopup, ItemImage, LoaderBar } from '../../../components';
import { ExpandCollapseToggle } from '../../shipments/Items/commonComponents';

const ItemsContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`;

const TitleContainer = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

export function PackingDetailItems({
  data,
  items,
  isPallet,
  packItem,
  fulfillItem,
  loading,
  confirmFulfillItem,
  isConfirmationRequired,
}) {
  const pendingItems = useMemo(() => {
    if (CommonUtility.isValidArray(items)) {
      return items?.filter(
        x =>
          !x.isCancelled && (isPallet ? x.status !== 'shipped' : x.status !== 'packed'),
      );
    }
    return [];
  }, [items, isPallet]);

  if (!isPallet && !pendingItems?.length) {
    return <></>;
  }

  return (
    <ShadowBox className="col-6">
      {loading && <LoaderBar />}
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <Tray size={24} color={theme.colors.grey} />
          <HeaderText className="mx-1">{isPallet ? 'Pallet' : 'Tote'}</HeaderText>
          <ColoredPill className="ml-2" color={theme.colors.grey}>
            <Barcode />
            {data?.toteNumber || data?.palletNumber}
          </ColoredPill>
        </div>
        <div className="d-flex align-items-center">
          <Cube color={theme.colors.grey} size={20} />
          <MuteText className="ml-2">{pendingItems?.length} Items</MuteText>
        </div>
      </div>
      <ItemsContainer>
        {pendingItems?.map((item, index) => (
          <Fragment key={item?.id}>
            <ItemRenderer
              item={item}
              isPallet={isPallet}
              packItem={packItem}
              fulfillItem={fulfillItem}
              isConfirmationRequired={isConfirmationRequired}
              confirmFulfillItem={confirmFulfillItem}
            />
            {index !== pendingItems?.length - 1 && <MuteBorder />}
          </Fragment>
        ))}
      </ItemsContainer>
    </ShadowBox>
  );
}

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 10px;
  .ui.image {
    min-width: 35px;
  }
`;

function ItemRenderer({
  item,
  isPallet,
  fulfillItem,
  packItem,
  confirmFulfillItem,
  isConfirmationRequired,
}) {
  const [open, setOpen] = useState(false);

  const hasOtherBarcodes = CommonUtility.isValidArray(
    item.productId?.barcode_identifiers,
  );

  return (
    <ItemRow>
      <div className="d-flex align-items-start">
        <ItemImage
          imageKey="p_images"
          displayImageKey="display_image"
          item={item?.productId}
          size="mini"
        />
        <div className="d-flex flex-column ml-2">
          <DarkPopup
            trigger={<TitleContainer>{item?.productId?.p_title}</TitleContainer>}
          >
            {item?.productId?.p_title}
          </DarkPopup>

          <div className="d-flex">
            <MuteText>SKU</MuteText>
            <span className="ml-2">{item?.productId?.p_sku}</span>
          </div>
          <div className="d-flex">
            <MuteText>UPC</MuteText>
            <span className="ml-2">{item?.productId?.upc}</span>
          </div>
          {hasOtherBarcodes && (
            <ExpandCollapseToggle
              title=""
              open={open}
              setOpen={setOpen}
              expandText="Show More"
              collapseText="Hide"
            />
          )}
          {open &&
            item.productId?.barcode_identifiers?.map(code => (
              <div className="d-flex align-items-center">
                <Barcode color={theme.colors.grey} size={17} />
                <span className="ml-1 mt-1">{code}</span>
              </div>
            ))}
        </div>
      </div>
      {isPallet && (
        <div className="d-flex mx-2">
          <MuteText className="mr-2">Order</MuteText>
          <span>{item.order?.marketplace_order_display_id}</span>
        </div>
      )}
      <ColoredPill color={theme.colors.grey}>
        {item.shipping?.labels?.[0]?.packagingMaterial?.name}
      </ColoredPill>
      {isPallet ? (
        <HoverBorderButton
          className="flex-btn px-3"
          onClick={() => {
            if (isConfirmationRequired) {
              confirmFulfillItem(item?.productId?.upc);
            } else {
              fulfillItem(item?.productId?.upc);
            }
          }}
        >
          <Truck className="mr-1" size={17} /> Fulfill
        </HoverBorderButton>
      ) : (
        <HoverBorderButton
          onClick={() => packItem(item?.productId?.upc)}
          className="flex-btn px-3"
        >
          <Package className="mr-1" size={17} /> Pack
        </HoverBorderButton>
      )}
    </ItemRow>
  );
}
